// /browse — region + country directory of the player corpus.
//
// M5.3 shipped country browse; M5.4 follow-up adds the region layer
// (handover queue item #4). Three routes now share this file:
//   - /browse                       → BrowseIndex (region + country list)
//   - /browse/region/:slug          → BrowseRegion (countries in region)
//   - /browse/country/:slug         → BrowseCountry (player grid)
//
// Region pages show country cards only — no player roll-up. The
// /api/players/search endpoint accepts a single `nationality` value,
// not multi-value OR; building a multi-country result would need a new
// `?nationalities=` param or a `?region=` resolver server-side. Both
// are deferrable since "tap a country card to see players" is the
// well-trodden one-extra-tap path. Skipping the API change keeps this
// pass strictly frontend + a small data file.
//
// League browse (/browse/league/:slug) is deliberately NOT here.
// player_history doesn't carry league/team data (it's the agent-
// representation timeline), and player_stats is sparse — populated
// only via syncPlayerStats, which the bulk discovery import didn't
// run. Until a league-affiliation source lands, league browse is
// out of scope. See SESSION_HANDOVER for the follow-up plan.

import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ChevronDown, SlidersHorizontal } from 'lucide-react'
import { useMeta } from '../hooks/useMeta'
import PlayerCard, { type PlayerSearchResult } from '../components/PlayerCard'
import {
  REGIONS,
  getRegionFor,
  getRegionMeta,
  type RegionMeta,
} from '../lib/regions'

interface CountryEntry {
  slug: string
  name: string
  player_count: number
}

interface CountriesResponse {
  countries: CountryEntry[]
}

// M6.2 — league directory entries from /api/players/leagues. Shape mirrors
// CountryEntry; player_count drives the featured top-5 ranking on
// BrowseIndex + the prioritization on the expand list.
//
// M6.2-followup — country added for display-name disambiguation. API-Sports
// ships two "Super Cup" leagues (Turkey + Greece) under the same `name`;
// without country context they render identically in dropdowns + card
// rows. See disambiguateLeagueName below.
interface LeagueEntry {
  slug: string
  name: string
  country: string | null
  player_count: number
}

interface LeaguesResponse {
  leagues: LeagueEntry[]
}

// M6.2-followup — programmatic display-name disambiguation. Detects
// collisions at render time so a future enrichment that lands a third
// duplicate name doesn't need a code change.
//
// Strategy:
//   - Single name → return name unchanged (the common case).
//   - Collision + country known → "Name (Country)".
//   - Collision + country null  → "Name (slug)" as a last-resort fallback
//     so the labels remain visually distinct even with bad upstream data.
//     Slug is unique by construction so this always disambiguates.
//
// O(N²) over the league list — N ≤ ~30 even with generous future imports,
// so a Map-prep optimization is premature.
export function disambiguateLeagueName(
  target: { name: string; country: string | null; slug: string },
  all: ReadonlyArray<{ name: string }>,
): string {
  const collisions = all.filter((l) => l.name === target.name).length
  if (collisions <= 1) return target.name
  if (target.country) return `${target.name} (${target.country})`
  return `${target.name} (${target.slug})`
}

interface SearchResponse {
  results: PlayerSearchResult[]
  has_more: boolean
}

const PAGE_SIZE = 24
// M6.2 — top N league cards shown above the expand fold on BrowseIndex.
// Reflects the M6.1 verification: ABA + Euroleague + ABA League 2 + NBL +
// Champions League cover 1952 / 2325 = 84% of the enriched corpus. The
// remaining 17 fit naturally inside an "All 22" disclosure.
const FEATURED_LEAGUES = 5

// ---- /browse ----------------------------------------------------------

export function BrowseIndex() {
  // Intentionally drops query strings — filter state shouldn't fragment
  // indexing. /browse has no query params today, but the pattern keeps
  // all three Browse canonical builders identical.
  const canonical = window.location.origin + window.location.pathname
  useMeta({
    title: 'Browse players — HoopRoster',
    description:
      'Browse basketball players by region or country on HoopRoster. Discover prospects and verified profiles.',
    canonical,
  })

  const [countries, setCountries] = useState<CountryEntry[] | null>(null)
  const [leagues, setLeagues] = useState<LeagueEntry[] | null>(null)
  const [leaguesExpanded, setLeaguesExpanded] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  // UIR-3 — track expanded state per region (first region expanded by default)
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set())
  // UIR-3 — track "show all countries" state per region
  const [showAllCountries, setShowAllCountries] = useState<Set<string>>(new Set())

  useEffect(() => {
    let cancelled = false
    // M6.2 — countries + leagues fetched in parallel. Both are 5-min
    // edge-cached so concurrent first-paint costs nothing extra.
    // Leagues failure is silently absorbed — the section just renders
    // "Loading…" and never resolves. Countries failure surfaces in the
    // existing err banner since BrowseIndex started life as
    // countries-only and that error path was already plumbed.
    Promise.all([
      fetch('/api/players/countries').then(async (r) => {
        if (!r.ok) throw new Error(`countries_failed_${r.status}`)
        return (await r.json()) as CountriesResponse
      }),
      fetch('/api/players/leagues').then(async (r) => {
        if (!r.ok) throw new Error(`leagues_failed_${r.status}`)
        return (await r.json()) as LeaguesResponse
      }),
    ])
      .then(([countryData, leagueData]) => {
        if (cancelled) return
        setCountries(countryData.countries ?? [])
        setLeagues(leagueData.leagues ?? [])
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'unknown_error')
      })
    return () => {
      cancelled = true
    }
  }, [])

  // M5.4 — region aggregation. Sum player_count for every country in
  // each region. Hidden regions (zero players in the corpus) are
  // filtered out so the cards don't render with "0 players" — keeps
  // the page from looking sparse on small corpora. Memoized off
  // `countries` so it only recomputes on the single fetch.
  // UIR-3 — enhanced to include countries array per region
  const regionData = useMemo(() => {
    if (countries == null) return null
    const data = new Map<
      string,
      { meta: RegionMeta; player_count: number; country_count: number; countries: CountryEntry[] }
    >()
    for (const region of REGIONS) {
      data.set(region.slug, {
        meta: region,
        player_count: 0,
        country_count: 0,
        countries: [],
      })
    }
    for (const c of countries) {
      const slug = getRegionFor(c.name)
      const bucket = data.get(slug)
      if (!bucket) continue
      bucket.player_count += c.player_count
      bucket.country_count += 1
      bucket.countries.push(c)
    }
    // Sort countries within each region by player_count DESC
    for (const bucket of data.values()) {
      bucket.countries.sort((a, b) => b.player_count - a.player_count)
    }
    const regions = Array.from(data.values()).filter((r) => r.player_count > 0)
    // Expand first region by default
    if (regions.length > 0 && expandedRegions.size === 0) {
      setExpandedRegions(new Set([regions[0].meta.slug]))
    }
    return regions
  }, [countries, expandedRegions.size])

  function toggleRegion(slug: string) {
    setExpandedRegions(prev => {
      const next = new Set(prev)
      if (next.has(slug)) {
        next.delete(slug)
      } else {
        next.add(slug)
      }
      return next
    })
  }

  function toggleShowAll(slug: string) {
    setShowAllCountries(prev => {
      const next = new Set(prev)
      if (next.has(slug)) {
        next.delete(slug)
      } else {
        next.add(slug)
      }
      return next
    })
  }

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-hoop-text">
          Browse players
        </h1>
        <p className="text-sm text-hoop-text-3 mt-2">
          Curated entry points into the HoopRoster corpus. Start broad
          with a region or drill into a specific country.
        </p>
      </header>

      {/* UIR-3 — Region accordions with country chips */}
      <section className="mb-10">
        <h2 className="text-lg font-semibold text-hoop-text mb-3">
          By region
        </h2>
        {err && (
          <div className="border border-hoop-red-bg bg-hoop-red-bg text-hoop-red rounded-md px-4 py-3 text-sm mb-3">
            {err}
          </div>
        )}
        {regionData == null ? (
          <p className="text-hoop-text-3 text-sm">Loading…</p>
        ) : regionData.length === 0 ? (
          <div className="border border-dashed border-hoop-border rounded-lg p-6 text-center text-sm text-hoop-text-3">
            No regions to browse yet.
          </div>
        ) : (
          <div className="space-y-2">
            {regionData.map((r) => {
              const isExpanded = expandedRegions.has(r.meta.slug)
              const showAll = showAllCountries.has(r.meta.slug)
              const visibleCountries = showAll ? r.countries : r.countries.slice(0, 10)
              const hasMore = r.countries.length > 10

              return (
                <div key={r.meta.slug} className="border border-hoop-border rounded-lg overflow-hidden bg-hoop-surface">
                  {/* Accordion header */}
                  <button
                    type="button"
                    onClick={() => toggleRegion(r.meta.slug)}
                    aria-expanded={isExpanded}
                    className="w-full px-4 py-3.5 flex items-center justify-between text-left hover:bg-hoop-border/20 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-base font-semibold text-hoop-text">
                        {r.meta.name}
                      </div>
                      <div className="text-xs text-hoop-text-3 mt-0.5">
                        {r.player_count} player{r.player_count === 1 ? '' : 's'}
                        {' · '}
                        {r.country_count} countr{r.country_count === 1 ? 'y' : 'ies'}
                      </div>
                    </div>
                    <ChevronDown
                      className={`w-4 h-4 text-hoop-text-3 transition-transform flex-shrink-0 ml-3 ${isExpanded ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {/* Accordion body - country chips */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-hoop-border">
                      <div className="flex flex-wrap gap-2 mt-3">
                        {visibleCountries.map((c) => (
                          <Link
                            key={c.slug}
                            to={`/browse/country/${encodeURIComponent(c.slug)}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-hoop-border bg-white dark:bg-hoop-surface text-sm text-hoop-text hover:border-hoop-accent hover:bg-hoop-accent/5 transition-colors"
                          >
                            <span className="font-medium">{c.name}</span>
                            <span className="text-hoop-text-3 text-xs">{c.player_count}</span>
                          </Link>
                        ))}
                        {hasMore && !showAll && (
                          <button
                            type="button"
                            onClick={() => toggleShowAll(r.meta.slug)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-hoop-border bg-white dark:bg-hoop-surface text-sm text-hoop-accent hover:border-hoop-accent hover:bg-hoop-accent/5 transition-colors font-medium"
                          >
                            All {r.country_count} countries
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </section>

      {/* M6.2 — league browse (replaces M5.3's placeholder; the
          player_history-lacks-league note resolved in M6.1 with the
          team_leagues junction + per-team current_team_id enrichment).
          Featured top-5 above the fold, all 22 behind a disclosure
          below — top 5 already cover ~84% of the enriched corpus. */}
      <section className="mt-10">
        <h2 className="text-lg font-semibold text-hoop-text mb-3">
          By league
        </h2>
        {leagues == null ? (
          <p className="text-hoop-text-3 text-sm">Loading…</p>
        ) : leagues.length === 0 ? (
          <div className="border border-dashed border-hoop-border rounded-lg p-6 text-center text-sm text-hoop-text-3">
            No leagues to browse yet.
          </div>
        ) : (
          <>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {leagues.slice(0, FEATURED_LEAGUES).map((l) => (
                <li key={l.slug}>
                  <Link
                    to={`/browse/league/${encodeURIComponent(l.slug)}`}
                    className="block bg-hoop-surface border border-hoop-border rounded-lg px-4 py-3 hover:border-hoop-accent transition-colors"
                  >
                    <div className="font-medium text-hoop-text">
                      {disambiguateLeagueName(l, leagues)}
                    </div>
                    <div className="text-xs text-hoop-text-3 mt-0.5">
                      {l.player_count} player{l.player_count === 1 ? '' : 's'}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
            {leagues.length > FEATURED_LEAGUES && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setLeaguesExpanded((v) => !v)}
                  className="text-xs font-medium text-hoop-text-2 hover:text-hoop-text inline-flex items-center gap-1"
                  aria-expanded={leaguesExpanded}
                >
                  <span aria-hidden>{leaguesExpanded ? '▾' : '▸'}</span>
                  {leaguesExpanded
                    ? 'Hide remaining leagues'
                    : `Show all ${leagues.length} leagues`}
                </button>
                {leaguesExpanded && (
                  <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {leagues.slice(FEATURED_LEAGUES).map((l) => (
                      <li key={l.slug}>
                        <Link
                          to={`/browse/league/${encodeURIComponent(l.slug)}`}
                          className="block bg-hoop-surface border border-hoop-border rounded-lg px-4 py-3 hover:border-hoop-accent transition-colors"
                        >
                          <div className="font-medium text-hoop-text">
                            {disambiguateLeagueName(l, leagues)}
                          </div>
                          <div className="text-xs text-hoop-text-3 mt-0.5">
                            {l.player_count} player{l.player_count === 1 ? '' : 's'}
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </section>
    </main>
  )
}

// ---- /browse/country/:slug --------------------------------------------

export function BrowseCountry() {
  const { slug } = useParams()

  // Resolve slug → country name client-side. Single fetch of the
  // /countries list (5-min edge cached) gives us both the human name
  // for the hero + the player_count for the meta description.
  const [country, setCountry] = useState<CountryEntry | null | 'pending'>('pending')
  const [results, setResults] = useState<PlayerSearchResult[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // 1. Resolve the slug.
  useEffect(() => {
    if (!slug) return
    let cancelled = false
    fetch('/api/players/countries')
      .then(async (r) => {
        if (!r.ok) throw new Error(`request_failed_${r.status}`)
        return (await r.json()) as CountriesResponse
      })
      .then((data) => {
        if (cancelled) return
        const match = data.countries.find((c) => c.slug === slug)
        setCountry(match ?? null)
      })
      .catch(() => {
        if (!cancelled) setCountry(null)
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  // 2. Once the country resolves, fetch the first page of players.
  useEffect(() => {
    if (country === 'pending' || country === null) return
    let cancelled = false
    setLoading(true)
    setErr(null)
    setOffset(0)
    const url = `/api/players/search?nationality=${encodeURIComponent(country.name)}&limit=${PAGE_SIZE}&offset=0`
    fetch(url)
      .then(async (r) => {
        if (!r.ok) throw new Error(`request_failed_${r.status}`)
        return (await r.json()) as SearchResponse
      })
      .then((data) => {
        if (cancelled) return
        setResults(data.results)
        setHasMore(data.has_more)
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'unknown_error')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [country])

  // Title/meta. Empty values when country is unresolved (the hook
  // short-circuits on falsy strings).
  const resolvedName = country && country !== 'pending' ? country.name : ''
  const resolvedCount = country && country !== 'pending' ? country.player_count : 0
  // Drop query strings — filter state shouldn't fragment indexing.
  // Gated on resolvedName so unresolved/404 routes don't claim canonical.
  const canonical = resolvedName
    ? window.location.origin + window.location.pathname
    : ''
  useMeta({
    title: resolvedName ? `${resolvedName} basketball players — HoopRoster` : '',
    description: resolvedName
      ? `${resolvedCount} basketball player${resolvedCount === 1 ? '' : 's'} from ${resolvedName} on HoopRoster — agents, prospects, and verified profiles.`
      : '',
    canonical,
  })

  async function loadMore() {
    if (loadingMore || !hasMore || country === 'pending' || country === null) return
    setLoadingMore(true)
    setErr(null)
    const nextOffset = offset + PAGE_SIZE
    try {
      const url = `/api/players/search?nationality=${encodeURIComponent(country.name)}&limit=${PAGE_SIZE}&offset=${nextOffset}`
      const res = await fetch(url)
      if (!res.ok) throw new Error(`request_failed_${res.status}`)
      const data = (await res.json()) as SearchResponse
      setResults((prev) => [...prev, ...data.results])
      setHasMore(data.has_more)
      setOffset(nextOffset)
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'unknown_error')
    } finally {
      setLoadingMore(false)
    }
  }

  if (country === 'pending') {
    return (
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 text-hoop-text-3 text-sm">
        Loading…
      </main>
    )
  }
  if (country === null) {
    return (
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <p className="text-hoop-text-2">Country not found.</p>
        <Link to="/browse" className="text-sm underline mt-2 inline-block">
          ← Back to browse
        </Link>
      </main>
    )
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <header className="mb-6">
        <nav className="text-xs text-hoop-text-3 mb-2">
          <Link to="/browse" className="hover:text-hoop-text">
            Browse
          </Link>
          <span> · </span>
          <span>Countries</span>
        </nav>
        <h1 className="text-2xl sm:text-3xl font-bold text-hoop-text">
          Players from {country.name}
        </h1>
        <p className="text-sm text-hoop-text-3 mt-1">
          {country.player_count} player{country.player_count === 1 ? '' : 's'} on HoopRoster
        </p>
        <Link
          to={`/discover?nationality=${encodeURIComponent(country.name)}`}
          className="inline-flex items-center gap-2 mt-3 px-4 py-2 text-[13px] font-semibold rounded-lg border border-hoop-border bg-hoop-surface text-hoop-text hover:border-hoop-accent hover:text-hoop-accent transition-colors"
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          Filter these players in Discover
        </Link>
      </header>

      {err && (
        <div className="border border-hoop-red-bg bg-hoop-red-bg text-hoop-red rounded-md px-4 py-3 text-sm mb-4">
          {err}
        </div>
      )}

      {loading ? (
        <p className="text-hoop-text-3 text-sm">Loading…</p>
      ) : results.length === 0 ? (
        <div className="border border-dashed border-hoop-border rounded-lg p-10 text-center text-sm text-hoop-text-3">
          No players from {country.name} yet.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((r) => (
              <PlayerCard key={r.id} player={r} />
            ))}
          </div>
          {hasMore && (
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={loadingMore}
                className="bg-hoop-primary text-hoop-primary-fg px-6 py-2 rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-40"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </main>
  )
}

// ---- /browse/region/:slug ---------------------------------------------

export function BrowseRegion() {
  const { slug } = useParams()

  // Resolve slug → region metadata via the static map. Returns null
  // for unknown slugs so the 404 branch can fire without a network
  // hop. Region list is tiny; doing this synchronously is fine.
  const region = useMemo(() => (slug ? getRegionMeta(slug) : null), [slug])

  const [allCountries, setAllCountries] = useState<CountryEntry[] | null>(null)
  const [err, setErr] = useState<string | null>(null)

  // Fetch the country list (same endpoint BrowseIndex + BrowseCountry
  // use — 5-min edge cached so a navigation through /browse → region
  // pays nothing extra). We still need it because the region → country
  // mapping lives client-side; the API doesn't know about regions.
  useEffect(() => {
    if (!region) return
    let cancelled = false
    fetch('/api/players/countries')
      .then(async (r) => {
        if (!r.ok) throw new Error(`request_failed_${r.status}`)
        return (await r.json()) as CountriesResponse
      })
      .then((data) => {
        if (cancelled) return
        setAllCountries(data.countries ?? [])
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'unknown_error')
      })
    return () => {
      cancelled = true
    }
  }, [region])

  // Filter countries to those that map to THIS region, then sort by
  // player_count DESC so the largest contributors lead. Ties broken
  // by name for stable order (matches the countries-endpoint sort).
  const countriesInRegion = useMemo(() => {
    if (allCountries == null || region == null) return null
    return allCountries
      .filter((c) => getRegionFor(c.name) === region.slug)
      .sort((a, b) => {
        if (b.player_count !== a.player_count) {
          return b.player_count - a.player_count
        }
        return a.name.localeCompare(b.name)
      })
  }, [allCountries, region])

  const totalPlayers = useMemo(() => {
    if (countriesInRegion == null) return 0
    return countriesInRegion.reduce((sum, c) => sum + c.player_count, 0)
  }, [countriesInRegion])

  // Meta tags. Empty title/description while loading so useMeta no-ops
  // until we know real numbers — avoids a flicker where the title
  // briefly says "0 players from 0 countries".
  const ready = region != null && countriesInRegion != null
  // Drop query strings — filter state shouldn't fragment indexing.
  // Gated on `ready` so unresolved/loading routes don't claim canonical.
  const canonical = ready
    ? window.location.origin + window.location.pathname
    : ''
  useMeta({
    title: ready ? `${region.name} basketball players — HoopRoster` : '',
    description: ready
      ? `${totalPlayers} basketball player${totalPlayers === 1 ? '' : 's'} from ${countriesInRegion.length} countr${countriesInRegion.length === 1 ? 'y' : 'ies'} across ${region.name} on HoopRoster — agents, prospects, and verified profiles.`
      : '',
    canonical,
  })

  if (region == null) {
    return (
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <p className="text-hoop-text-2">Region not found.</p>
        <Link to="/browse" className="text-sm underline mt-2 inline-block">
          ← Back to browse
        </Link>
      </main>
    )
  }

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <header className="mb-6">
        <nav className="text-xs text-hoop-text-3 mb-2">
          <Link to="/browse" className="hover:text-hoop-text">
            Browse
          </Link>
          <span> · </span>
          <span>Regions</span>
        </nav>
        <h1 className="text-2xl sm:text-3xl font-bold text-hoop-text">
          {region.name}
        </h1>
        {countriesInRegion != null && (
          <p className="text-sm text-hoop-text-3 mt-1">
            {totalPlayers} player{totalPlayers === 1 ? '' : 's'} across{' '}
            {countriesInRegion.length} countr
            {countriesInRegion.length === 1 ? 'y' : 'ies'} on HoopRoster
          </p>
        )}
        {region.blurb && (
          <p className="text-xs text-hoop-text-3 mt-2 max-w-2xl">
            {region.blurb}
          </p>
        )}
      </header>

      {err && (
        <div className="border border-hoop-red-bg bg-hoop-red-bg text-hoop-red rounded-md px-4 py-3 text-sm mb-4">
          {err}
        </div>
      )}

      {countriesInRegion == null ? (
        <p className="text-hoop-text-3 text-sm">Loading…</p>
      ) : countriesInRegion.length === 0 ? (
        <div className="border border-dashed border-hoop-border rounded-lg p-10 text-center text-sm text-hoop-text-3">
          No countries in {region.name} have players yet.
        </div>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {countriesInRegion.map((c) => (
            <li key={c.slug}>
              <Link
                to={`/browse/country/${encodeURIComponent(c.slug)}`}
                className="block bg-hoop-surface border border-hoop-border rounded-lg px-4 py-3 hover:border-hoop-accent transition-colors"
              >
                <div className="font-medium text-hoop-text">
                  {c.name}
                </div>
                <div className="text-xs text-hoop-text-3 mt-0.5">
                  {c.player_count} player{c.player_count === 1 ? '' : 's'}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  )
}

// ---- /browse/league/:slug ---------------------------------------------
//
// M6.2. Same shape as BrowseCountry — slug resolution against a 5-min
// edge-cached endpoint, then a paginated player grid. The two pages
// share PlayerCard + the useMeta+canonical pattern. They diverge only
// in the API surfaces they query.

// M6.2-followup — BrowseLeague stores both the matched entry AND the
// pre-computed display name (disambiguated against the full list at
// resolution time). H1, <title>, and <meta description> all consume
// displayName so two "Super Cup" slugs surface with country context.
interface ResolvedLeague {
  entry: LeagueEntry
  displayName: string
}

export function BrowseLeague() {
  const { slug } = useParams()

  // Resolve slug → league entry. Same one-fetch pattern as
  // BrowseCountry — /api/players/leagues is 5-min edge-cached so the
  // BrowseIndex → BrowseLeague nav pays nothing extra after first load.
  const [league, setLeague] = useState<ResolvedLeague | null | 'pending'>('pending')
  const [results, setResults] = useState<PlayerSearchResult[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // 1. Resolve the slug.
  useEffect(() => {
    if (!slug) return
    let cancelled = false
    fetch('/api/players/leagues')
      .then(async (r) => {
        if (!r.ok) throw new Error(`request_failed_${r.status}`)
        return (await r.json()) as LeaguesResponse
      })
      .then((data) => {
        if (cancelled) return
        const match = data.leagues.find((l) => l.slug === slug)
        if (match) {
          setLeague({
            entry: match,
            displayName: disambiguateLeagueName(match, data.leagues),
          })
        } else {
          setLeague(null)
        }
      })
      .catch(() => {
        if (!cancelled) setLeague(null)
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  // 2. Once the league resolves, fetch the first page of players.
  useEffect(() => {
    if (league === 'pending' || league === null) return
    let cancelled = false
    setLoading(true)
    setErr(null)
    setOffset(0)
    const url = `/api/players/search?league=${encodeURIComponent(league.entry.slug)}&limit=${PAGE_SIZE}&offset=0`
    fetch(url)
      .then(async (r) => {
        if (!r.ok) throw new Error(`request_failed_${r.status}`)
        return (await r.json()) as SearchResponse
      })
      .then((data) => {
        if (cancelled) return
        setResults(data.results)
        setHasMore(data.has_more)
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'unknown_error')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [league])

  // Title / meta / canonical. Same gating pattern as BrowseCountry —
  // unresolved or 404 routes don't claim canonical. Per M5.4 ce699e9
  // we intentionally drop query strings from the canonical href.
  // displayName carries the disambiguation for colliding names ("Super
  // Cup (Turkey)" vs "Super Cup (Greece)") so both the H1 and the SEO
  // surfaces remain distinct.
  const resolvedDisplay = league && league !== 'pending' ? league.displayName : ''
  const resolvedCount = league && league !== 'pending' ? league.entry.player_count : 0
  const canonical = resolvedDisplay
    ? window.location.origin + window.location.pathname
    : ''
  useMeta({
    title: resolvedDisplay
      ? `${resolvedDisplay} basketball players — HoopRoster`
      : '',
    description: resolvedDisplay
      ? `${resolvedCount} basketball player${resolvedCount === 1 ? '' : 's'} currently playing in ${resolvedDisplay} on HoopRoster — agents, prospects, and verified profiles.`
      : '',
    canonical,
  })

  async function loadMore() {
    if (loadingMore || !hasMore || league === 'pending' || league === null) return
    setLoadingMore(true)
    setErr(null)
    const nextOffset = offset + PAGE_SIZE
    try {
      const url = `/api/players/search?league=${encodeURIComponent(league.entry.slug)}&limit=${PAGE_SIZE}&offset=${nextOffset}`
      const res = await fetch(url)
      if (!res.ok) throw new Error(`request_failed_${res.status}`)
      const data = (await res.json()) as SearchResponse
      setResults((prev) => [...prev, ...data.results])
      setHasMore(data.has_more)
      setOffset(nextOffset)
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'unknown_error')
    } finally {
      setLoadingMore(false)
    }
  }

  if (league === 'pending') {
    return (
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10 text-hoop-text-3 text-sm">
        Loading…
      </main>
    )
  }
  if (league === null) {
    return (
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <p className="text-hoop-text-2">League not found.</p>
        <Link to="/browse" className="text-sm underline mt-2 inline-block">
          ← Back to browse
        </Link>
      </main>
    )
  }

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <header className="mb-6">
        <nav className="text-xs text-hoop-text-3 mb-2">
          <Link to="/browse" className="hover:text-hoop-text">
            Browse
          </Link>
          <span> · </span>
          <span>Leagues</span>
        </nav>
        <h1 className="text-2xl sm:text-3xl font-bold text-hoop-text">
          {league.displayName}
        </h1>
        <p className="text-sm text-hoop-text-3 mt-1">
          {league.entry.player_count} player{league.entry.player_count === 1 ? '' : 's'} currently in this league
        </p>
        <Link
          to={`/discover?league=${encodeURIComponent(league.entry.slug)}`}
          className="inline-flex items-center gap-2 mt-3 px-4 py-2 text-[13px] font-semibold rounded-lg border border-hoop-border bg-hoop-surface text-hoop-text hover:border-hoop-accent hover:text-hoop-accent transition-colors"
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          Filter these players in Discover
        </Link>
      </header>

      {err && (
        <div className="border border-hoop-red-bg bg-hoop-red-bg text-hoop-red rounded-md px-4 py-3 text-sm mb-4">
          {err}
        </div>
      )}

      {loading ? (
        <p className="text-hoop-text-3 text-sm">Loading…</p>
      ) : results.length === 0 ? (
        <div className="border border-dashed border-hoop-border rounded-lg p-10 text-center text-sm text-hoop-text-3">
          No players currently in {league.displayName}.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.map((r) => (
              <PlayerCard key={r.id} player={r} />
            ))}
          </div>
          {hasMore && (
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={loadingMore}
                className="bg-hoop-primary text-hoop-primary-fg px-6 py-2 rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-40"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </main>
  )
}
